import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { TrackChanges } from '@mui/icons-material';
import { useGetLandRegisterAccesses } from '../../hooks/reactQuerys/FederalStateLoginQueries';
import {
  ILandRegisterTrackingCredential,
  ILandRegisterTrackingRunResult,
  useGetLandRegisterTrackingDue,
  useRunLandRegisterTracking,
} from '../../hooks/reactQuerys/LandMarkingQueries';

interface IProps {
  /** Wird nach erfolgreicher Nachverfolgung aufgerufen (z. B. Liste neu laden). */
  onCompleted?: () => void;
}

interface ISelectionEntry {
  idFederalStateLogin: number | '';
  password: string;
}

const formatDateTime = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString('de-DE');
};

/**
 * Manuelle Grundbuch-Nachverfolgung: Der Mitarbeiter wählt je fälligem
 * Bundesland einen Zugang + Passwort (oder überspringt es) und startet die
 * Nachverfolgung. Anschließend wird eine Ergebnisliste angezeigt.
 */
export const LandRegisterTrackingRunDialog: React.FC<IProps> = ({
  onCompleted,
}) => {
  const [open, setOpen] = useState(false);
  const { trackingDue, isLoadingTrackingDue } =
    useGetLandRegisterTrackingDue(open);
  const { landRegisterAccesses } = useGetLandRegisterAccesses();
  const { runLandRegisterTracking, isRunningLandRegisterTracking } =
    useRunLandRegisterTracking();

  const [selection, setSelection] = useState<Record<number, ISelectionEntry>>(
    {}
  );
  const [result, setResult] = useState<ILandRegisterTrackingRunResult | null>(
    null
  );

  useEffect(() => {
    if (open && trackingDue) {
      const init: Record<number, ISelectionEntry> = {};
      trackingDue.due.forEach(d => {
        init[d.idFederalState] = { idFederalStateLogin: '', password: '' };
      });
      setSelection(init);
      setResult(null);
    }
  }, [open, trackingDue]);

  const accessesFor = (idFederalState: number) =>
    (landRegisterAccesses ?? []).filter(
      a => a.idFederalState === idFederalState
    );

  const setEntry = (idFederalState: number, patch: Partial<ISelectionEntry>) =>
    setSelection(prev => ({
      ...prev,
      [idFederalState]: { ...prev[idFederalState], ...patch },
    }));

  const handleRun = async () => {
    const credentials: ILandRegisterTrackingCredential[] = [];
    (trackingDue?.due ?? []).forEach(d => {
      const sel = selection[d.idFederalState];
      if (sel && sel.idFederalStateLogin !== '' && sel.password) {
        credentials.push({
          idFederalState: d.idFederalState,
          idFederalStateLogin: Number(sel.idFederalStateLogin),
          password: sel.password,
        });
      }
    });
    const res = await runLandRegisterTracking(credentials);
    setResult(res);
    onCompleted?.();
  };

  const due = trackingDue?.due ?? [];
  const hasSelectable = due.some(d => {
    const sel = selection[d.idFederalState];
    return sel && sel.idFederalStateLogin !== '' && sel.password;
  });

  return (
    <>
      <Button
        variant='contained'
        startIcon={<TrackChanges />}
        onClick={() => setOpen(true)}
      >
        Grundbuchnachverfolgungen durchführen
      </Button>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth='md'
        fullWidth
      >
        <DialogTitle>Grundbuchnachverfolgungen durchführen</DialogTitle>
        <DialogContent dividers>
          {isLoadingTrackingDue ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress />
            </Box>
          ) : result ? (
            /* ---------- Ergebnisliste ---------- */
            <Stack spacing={2}>
              <Alert severity='success'>
                Die Nachverfolgung wurde durchgeführt.
              </Alert>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>Bundesland</TableCell>
                    <TableCell align='right'>Geprüft</TableCell>
                    <TableCell align='right'>Änderungen</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.results.map(r => (
                    <TableRow key={`res-${r.idFederalState}`}>
                      <TableCell>
                        {r.FederalState ?? `#${r.idFederalState}`}
                      </TableCell>
                      <TableCell align='right'>{r.processed ?? 0}</TableCell>
                      <TableCell align='right'>{r.found ?? 0}</TableCell>
                      <TableCell>
                        {r.skipped ? (
                          <Chip
                            size='small'
                            label={`übersprungen (${r.dueCount ?? 0} offen)`}
                          />
                        ) : r.error ? (
                          <Chip size='small' color='error' label='Fehler' />
                        ) : (
                          <Chip
                            size='small'
                            color={r.errors ? 'warning' : 'success'}
                            label={r.errors ? `${r.errors} Fehler` : 'OK'}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Typography variant='body2' color='text.secondary'>
                Nächste empfohlene Prüfung:{' '}
                {formatDateTime(result.nextRecommendedCheck)}
              </Typography>
            </Stack>
          ) : due.length === 0 ? (
            /* ---------- Nichts fällig ---------- */
            <Stack spacing={2}>
              <Alert severity='info'>
                Aktuell sind keine Grundbuchnachverfolgungen fällig.
              </Alert>
              <Typography variant='body2' color='text.secondary'>
                Nächste empfohlene Prüfung:{' '}
                {formatDateTime(trackingDue?.nextRecommendedCheck)}
              </Typography>
            </Stack>
          ) : (
            /* ---------- Auswahl ---------- */
            <Stack spacing={2}>
              <Typography variant='body2' color='text.secondary'>
                Wählen Sie je Bundesland einen Zugang und geben Sie Ihr Passwort
                ein. Bundesländer ohne Zugang werden übersprungen.
              </Typography>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>Bundesland</TableCell>
                    <TableCell align='right'>Fällig</TableCell>
                    <TableCell>Zugang</TableCell>
                    <TableCell>Passwort</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {due.map(d => {
                    const accesses = accessesFor(d.idFederalState);
                    const sel = selection[d.idFederalState] ?? {
                      idFederalStateLogin: '',
                      password: '',
                    };
                    return (
                      <TableRow key={`due-${d.idFederalState}`}>
                        <TableCell>
                          {d.FederalState ?? `#${d.idFederalState}`}
                        </TableCell>
                        <TableCell align='right'>{d.dueCount}</TableCell>
                        <TableCell sx={{ minWidth: 200 }}>
                          {accesses.length === 0 ? (
                            <Typography
                              variant='caption'
                              color='text.secondary'
                            >
                              Kein Zugang – wird übersprungen
                            </Typography>
                          ) : (
                            <FormControl fullWidth size='small'>
                              <InputLabel id={`acc-${d.idFederalState}`}>
                                Zugang
                              </InputLabel>
                              <Select
                                labelId={`acc-${d.idFederalState}`}
                                label='Zugang'
                                value={sel.idFederalStateLogin}
                                onChange={e =>
                                  setEntry(d.idFederalState, {
                                    idFederalStateLogin: e.target
                                      .value as number,
                                  })
                                }
                              >
                                {accesses.map(a => (
                                  <MenuItem
                                    key={`acc-opt-${a.idFederalStateLogin}`}
                                    value={a.idFederalStateLogin}
                                  >
                                    {a.Username}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}
                        </TableCell>
                        <TableCell sx={{ minWidth: 180 }}>
                          <TextField
                            size='small'
                            type='password'
                            fullWidth
                            disabled={accesses.length === 0}
                            value={sel.password}
                            onChange={e =>
                              setEntry(d.idFederalState, {
                                password: e.target.value,
                              })
                            }
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <Typography variant='body2' color='text.secondary'>
                Nächste empfohlene Prüfung:{' '}
                {formatDateTime(trackingDue?.nextRecommendedCheck)}
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant='outlined' onClick={() => setOpen(false)}>
            {result ? 'Schließen' : 'Abbruch'}
          </Button>
          {!result && due.length > 0 && (
            <Button
              variant='contained'
              onClick={handleRun}
              disabled={!hasSelectable || isRunningLandRegisterTracking}
            >
              {isRunningLandRegisterTracking ? (
                <Box display='flex' alignItems='center' gap={1}>
                  <span>Wird durchgeführt …</span>
                  <CircularProgress size={18} />
                </Box>
              ) : (
                'Nachverfolgung starten'
              )}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};
